import React, { useEffect } from 'react'
import {
  BoxProps,
  useForm,
  FormProvider,
  Stack,
  LinkButton,
  Flex,
  TextInput,
  Button,
  useWatch,
} from '@thirstycamel/ui'
import type { UserLoginDto } from '@thirsty-camel/hump-club/src/modules/auth/dto/UserLoginDto'

import { REGEX_MOBILE_NUMBER, REGEX_MOBILE_NUMBER_OR_EMAIL } from '../../utils/regex'
import Input from '../Input'
import FormError from '../FormError'
import { useActions, useStore } from '../../store/hooks'
import { eventGA4 } from '../../utils/gtag'

export interface LoginFormProps extends BoxProps {
  onSuccess?: () => void
}

export const LoginForm = ({ onSuccess, ...restProps }: LoginFormProps) => {
  const form = useForm<UserLoginDto>()

  const { login, setStatus } = useActions(actions => actions.auth)
  const { isFetching, error } = useStore(store => store.auth)

  const mobileNumber = useWatch({ name: 'mobileNumber', control: form.control, defaultValue: '' })

  /* Reset the error state when this form is unmounted. */
  useEffect(() => {
    return () => setStatus({ error: null, isFetching: false })
  }, [])

  const handleSubmit = form.handleSubmit(async values => {
    /* Determine whether email or mobile */
    const isMobile = REGEX_MOBILE_NUMBER.test(values.mobileNumber)

    const isSuccessful = await login({
      [isMobile ? 'mobileNumber' : 'email']: values.mobileNumber,
      password: values.password,
    })

    if (isSuccessful) {
      onSuccess && onSuccess()

      /* Send to Google Analytics */
      eventGA4('login', { event_label: 'Logged into Hump Club', method: 'email' })
    }
  })

  return (
    <FormProvider {...form}>
      <Stack as="form" spacing={5} onSubmit={handleSubmit} {...restProps}>
        <Stack spacing={4}>
          <Input
            as={TextInput}
            isRequired
            name="mobileNumber"
            type="tel"
            placeholder="MOBILE NUMBER"
            _placeholder={{ fontFamily: 'heading' }}
            isReadOnly={isFetching}
            rules={{
              required: 'Please enter your mobile number.',
              pattern: {
                value: REGEX_MOBILE_NUMBER_OR_EMAIL,
                message: 'Please enter a valid Australian mobile number.',
              },
            }}
          />

          <Input
            as={TextInput}
            isRequired
            type="password"
            name="password"
            placeholder="PASSWORD"
            _placeholder={{ fontFamily: 'heading' }}
            isReadOnly={isFetching}
            rules={{ required: 'Please enter your password.' }}
          />
        </Stack>

        {error && <FormError variant="left-accent" errors={error} />}

        <Stack spacing={[4, 3]} align={['center', 'flex-start']}>
          <Flex direction={['column', 'row']} justify="space-between" align="center" w="100%">
            {/* <Input
              as={Checkbox}
              name="rememberMe"
              fontFamily="heading"
              textTransform="uppercase"
              color="blackAlpha.600"
              isReadOnly={isFetching}
              mb={[5, 0]}
            >
              Remember me
            </Input> */}

            <Button
              type="submit"
              variantColor="pink"
              minWidth="10rem"
              w="100%"
              isLoading={isFetching}
            >
              Sign in
            </Button>
          </Flex>

          <LinkButton
            href={`/forgot-password${mobileNumber ? `?mobile=${mobileNumber}` : ''}`}
            variant="ghost"
            px={1}
            color="pink.500"
          >
            Forgot password?
          </LinkButton>
        </Stack>
      </Stack>
    </FormProvider>
  )
}

export default LoginForm
